import { toast } from 'sonner'
import i18n from './i18n'
import { supabase } from './supabase'
import { onEvent, fireEvent, EVENT_CATEGORIES, type MoriEventName } from './events'

// ── Types ─────────────────────────────────────────────────────────────────────

interface AcornRule {
  id: string
  event_name: string
  condition_key: string | null
  condition_value: string | null
  acorn_amount: number
  label: string
  active: boolean
}

// ── Rule cache (5-minute TTL) ─────────────────────────────────────────────────

let rulesCache: AcornRule[] | null = null
let rulesCachedAt = 0
const CACHE_TTL = 5 * 60 * 1000

async function getAcornRules(): Promise<AcornRule[]> {
  const now = Date.now()
  if (rulesCache && now - rulesCachedAt < CACHE_TTL) {
    return rulesCache
  }
  const { data } = await supabase
    .from('acorn_rules')
    .select('id, event_name, condition_key, condition_value, acorn_amount, label, active')
    .eq('active', true)
    .order('sort_order')
  rulesCache = (data as AcornRule[] | null) ?? []
  rulesCachedAt = now
  return rulesCache
}

export function invalidateAcornRulesCache(): void {
  rulesCache = null
  rulesCachedAt = 0
}

// ── Condition evaluator ───────────────────────────────────────────────────────

function evaluateCondition(
  rule: AcornRule,
  payload: Record<string, unknown>,
): boolean {
  if (!rule.condition_key) return true
  const actual = String(payload[rule.condition_key] ?? '')
  return actual === rule.condition_value
}

// ── Core processor ────────────────────────────────────────────────────────────

async function processEvent(
  eventName: MoriEventName,
  payload: Record<string, unknown>,
): Promise<void> {
  try {
    const rules = await getAcornRules()
    const matching = rules.filter(
      (r) => r.event_name === eventName && evaluateCondition(r, payload),
    )

    for (const rule of matching) {
      // Fixed-amount rule: pass exact value; RPC validates it matches.
      // Variable-amount rule (acorn_amount === -1): pass null; RPC resolves
      // server-side from collection_templates. Only collection.completed is
      // supported — any other event with a -1 rule is skipped with a warning.
      let amount: number | null
      if (rule.acorn_amount === -1) {
        if (eventName !== 'collection.completed' || !payload.collectionId) {
          console.warn(
            '[acornSubscribers] skipping rule with acorn_amount=-1 — no server resolver for event',
            eventName, rule.id
          )
          continue
        }
        amount = null
      } else {
        if (rule.acorn_amount <= 0) continue
        amount = rule.acorn_amount
      }

      const userId = payload.userId as string

      // Atomic balance + audit row via SECURITY DEFINER RPC.
      // The RPC derives user_id from auth.uid() server-side and writes the
      // acorn_transactions row inside the same transaction — closes F13
      // (clients can no longer forge audit rows directly).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: rpcData } = await (supabase.rpc as any)('award_acorns', {
        p_amount:        amount,
        p_reason:        rule.label,
        p_event_name:    eventName,
        p_rule_id:       rule.id,
        p_memory_id:     (payload.memoryId as string | undefined) ?? null,
        p_collection_id: (payload.collectionId as string | undefined) ?? null,
        p_to_tier:       (payload.toTier as string | undefined) ?? null,
      })
      const row = (rpcData as Array<{ new_balance: number; awarded: number }> | null)?.[0]
      const awarded = row?.awarded ?? 0
      const newBalance = row?.new_balance ?? 0

      // DB blocked a duplicate — suppress both the event and the toast.
      if (awarded === 0) continue

      // Notify the rest of the app (AcornContext listens to update balance)
      fireEvent('acorns.awarded', {
        userId,
        amount: awarded,
        reason: rule.label,
        source: EVENT_CATEGORIES[eventName],
        newBalance,
      })

      toast(i18n.t('mori.acornToast', { count: awarded }))
    }
  } catch (err) {
    // Never let acorn logic crash the app
    console.error('[acornSubscribers] error:', err)
  }
}

// ── Subscribe to all events that can award acorns ─────────────────────────────

export function initAcornSubscribers(): void {
  onEvent('memory.created',        (p) => processEvent('memory.created', p as Record<string, unknown>))
  onEvent('challenge.completed',   (p) => processEvent('challenge.completed', p as Record<string, unknown>))
  onEvent('collection.completed',  (p) => processEvent('collection.completed', p as Record<string, unknown>))
  onEvent('streak.updated',        (p) => processEvent('streak.updated', p as Record<string, unknown>))
  onEvent('onboarding.completed',  (p) => processEvent('onboarding.completed', p as Record<string, unknown>))
  onEvent('tribe.invite.accepted', (p) => processEvent('tribe.invite.accepted', p as Record<string, unknown>))
  onEvent('child.birthday.today',  (p) => processEvent('child.birthday.today', p as Record<string, unknown>))
  onEvent('subscription.upgraded', (p) => processEvent('subscription.upgraded', p as Record<string, unknown>))
  onEvent('intro.completed',       (p) => processEvent('intro.completed', p as Record<string, unknown>))
  onEvent('feedback.submitted',    (p) => processEvent('feedback.submitted', p as Record<string, unknown>))
}
